import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation21() {
  const { lesson, course, accent } = findLesson("piano-foundation", "21");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>The exercise is built on <strong>pivot notes</strong> — the root and the fifth of the scale (D and A in D major). They anchor everything.</Highlight>
        <Highlight>Three zones lay out around the pivots: <strong>high</strong> (above the fifth), <strong>middle</strong> (between root and fifth), <strong>low</strong> (below the root).</Highlight>
        <Highlight>For each zone, play a small arpeggio shape — <strong>low · middle · high · middle</strong> — moving across the zone's notes.</Highlight>
        <Highlight>Use <strong>different fingers</strong> for the pivots in each zone — thumb-pinky for some, two thumbs for others. The pivot fingering changes; the pivot notes don't.</Highlight>
        <Highlight>This is a <strong>compositional tool</strong> — we use it to inspire melodic ideas in any scale. The arpeggio shape is just the framework.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Pivot notes" title="Root and fifth — the anchors" accent={accent.hex}>
        <p>
          The Zone exercise sits on top of the fifth-chord exercise from earlier. Take the
          root and the fifth of whichever scale you're in — the demonstration uses{" "}
          <strong>D major</strong>, so the pivots are <strong>D and A</strong>. No octave
          this time; just the two pivot notes.
        </p>

        <Vocab>
          <Term name="Pivot set" def="root + fifth of the scale. The two stable notes the zones build around." />
          <Term name="Zone" def="a region of the keyboard relative to the pivots — high, middle, or low." />
          <Term name="Floating notes" def="the non-pivot notes inside a zone — these are what you arpeggiate." />
          <Term name="Keyboard geography" def="the term — the felt-sense of where each scale lives across the instrument." />
        </Vocab>
      </Insight>

      <Insight eyebrow="The three zones" title="High · Middle · Low — anchored to D and A" accent={accent.hex}>
        <p>
          With D as the root and A as the fifth, the three zones (in D major) are:
        </p>

        <ul className="space-y-1 text-[14px]">
          <li>
            <strong>High zone</strong> — notes to the right of the fifth: B, C♯, D (octave above
            root). If your hand stretches, sneak in the high E too.
          </li>
          <li>
            <strong>Middle zone</strong> — notes between the pivots: E, F♯, G. Play the pivots
            with thumb and pinky so the three middle fingers handle the floating notes.
          </li>
          <li>
            <strong>Low zone</strong> — notes to the left of the root: C♯, B, A (octave below).
            Use the thumb to stretch wide — it's the most stretchable finger.
          </li>
        </ul>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "D4", color: "#22c55e", label: "Pivot · D" },
            { pitch: "A4", color: "#22c55e", label: "Pivot · A" },
            { pitch: "E4", color: "#3b82f6", label: "M" },
            { pitch: "F#4", color: "#3b82f6", label: "M" },
            { pitch: "G4", color: "#3b82f6", label: "M" },
            { pitch: "B4", color: "#a855f7", label: "H" },
            { pitch: "C#5", color: "#a855f7", label: "H" },
            { pitch: "C#4", color: "#ec4899", label: "L" },
            { pitch: "B3", color: "#ec4899", label: "L" },
          ]}
          height={200}
        />

        <p className="prose-nsm text-sm">
          <span className="text-emerald-300">Green</span> = pivots (D and A).{" "}
          <span className="text-blue-300">Blue</span> = middle zone (E-F♯-G).{" "}
          <span className="text-purple-300">Purple</span> = high zone (B-C♯).{" "}
          <span className="text-pink-300">Pink</span> = low zone (C♯-B).
        </p>
      </Insight>

      <Insight eyebrow="The pattern" title="L · M · H · M — within each zone" accent={accent.hex}>
        <p>
          Inside any one zone, play a small arpeggio — <strong>Low · Middle · High · Middle</strong>{" "}
          — using the floating notes plus the upper pivot. It's the same LMHM idea from the
          arpeggio lesson, just framed by the pivots.
        </p>

        <p>
          For the <strong>high zone</strong> in D major: play A (pivot) → B → C♯ → D, then
          back. Or start from the top: D → C♯ → B → A. Two directions, same notes.
        </p>

        <KeyIdea>
          The pivot fingering changes between zones, but the <em>arpeggio shape</em> doesn't.
          Once you have the L-M-H-M pattern in your hand, you can pour it into any zone of any
          scale. That's why this is a <em>compositional</em> tool, not just a finger drill.
        </KeyIdea>
      </Insight>

      <Insight eyebrow="The left hand" title="Hold the Sa, then explore" accent={accent.hex}>
        <p>
          The left hand keeps things simple: hold the root (Sa) on the pulse — D in D major.
          Once the right-hand zone exploration is comfortable, the left hand can also start
          changing the bass to other scale notes — G, A, etc. — adding harmony to the
          melodic shape on top.
        </p>

        <Pull>
          The bass could also change, but change the bass once you get comfortable with
          floating the zone.
        </Pull>
      </Insight>

      <Insight eyebrow="Combine the zones" title="High meets low — your mind decides" accent={accent.hex}>
        <p>
          After exploring each zone alone, combine them. Two bars of high zone, then two of
          middle, then two of low. Or jump high → low → middle in any order. The pivots stay
          the same; only the fingering and the floating notes shift.
        </p>

        <p>
          Sing what you play — the intervals will start to feel like a melody is forming.
          Here's the framing: this exercise is more about <strong>composition</strong> than
          drill. Each zone gives you a different region of the scale to draw from; combining
          them gives you a way to construct phrases.
        </p>
      </Insight>

      <Insight eyebrow="Practice path" title="One zone, then all three, then any scale" accent={accent.hex}>
        <Steps>
          <Step>
            <strong>Pick a scale</strong>. D major is the choice here. Find the root and
            fifth — your pivots.
          </Step>
          <Step>
            <strong>Drill the high zone</strong> with the L-M-H-M arpeggio. LH holds the
            root.
          </Step>
          <Step>
            <strong>Drill the middle zone</strong>. New fingering for the pivots — usually
            thumb-pinky. The middle three fingers work.
          </Step>
          <Step>
            <strong>Drill the low zone</strong>. Use the thumb's stretch.
          </Step>
          <Step>
            <strong>Combine</strong> — two bars of one zone, two bars of another. Sing along.
            Make up melodies.
          </Step>
          <Step>
            <strong>Move to another scale</strong> — G major, F major, anything. New pivots,
            same three-zone framework.
          </Step>
        </Steps>

        <Tip>
          End every session with <strong>your own stamp</strong> on the exercise — improvise
          a phrase using whatever zone material feels right. "Always look at putting your own stamp on the exercise. Try to make it your own."
        </Tip>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-22"
        label="Lesson 22 · Practicing Thirds"
        why="The Zone exercise gives you a melodic framework. The next exercise sharpens a specific interval — the third — which is the building block of every chord."
      />
    </LessonShell>
  );
}
